import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useLogo } from '../hooks/useLogo';

// Mostra apenas no primeiro carregamento da sessão (F5 / nova aba). Navegação interna pula.
let hasShownInCurrentPageLoad = false;

export function hasSessionPreLoaderShown() {
  return hasShownInCurrentPageLoad;
}

const MIN_VISIBLE_MS = 600;
const EXIT_ANIMATION_MS = 350;
const MAX_PROGRESS_WHILE_LOADING = 88;

const PreLoader = ({ isLoading, onLoadingComplete, forceShow = false }) => {
  const { companyName, primaryColor } = useCompanySettings();
  const { logo } = useLogo('header');
  // forceShow: Home sempre exibe, mesmo após navegação interna na sessão
  const skipPreloader = useRef(forceShow ? false : hasShownInCurrentPageLoad);
  // Primeiro paint já cobre navbar/footer — evita flash no F5
  const [visible, setVisible] = useState(() => !skipPreloader.current);
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);
  const shownAtRef = useRef(skipPreloader.current ? null : Date.now());
  const rafRef = useRef(null);
  const finishCalledRef = useRef(false);

  // Pré-carrega o logo enquanto o overlay está visível
  useEffect(() => {
    if (!logo) return;
    const img = new Image();
    img.src = logo;
  }, [logo]);

  const finishAndHide = useCallback(() => {
    if (finishCalledRef.current) return;
    finishCalledRef.current = true;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    setProgress(100);
    setExiting(true);

    window.setTimeout(() => {
      setVisible(false);
      hasShownInCurrentPageLoad = true;
      onLoadingComplete?.();
    }, EXIT_ANIMATION_MS);
  }, [onLoadingComplete]);

  // Navegação interna: não exibe e libera a página imediatamente
  useEffect(() => {
    if (skipPreloader.current && isLoading) {
      onLoadingComplete?.();
    }
  }, [isLoading, onLoadingComplete]);

  // Abre o overlay quando a página sinaliza que ainda está carregando
  useEffect(() => {
    if (skipPreloader.current) return;
    if (isLoading && !visible && !exiting) {
      shownAtRef.current = Date.now();
      finishCalledRef.current = false;
      setVisible(true);
      setProgress(0);
      setExiting(false);
    }
  }, [isLoading, visible, exiting]);

  // Progresso real: avança suavemente enquanto isLoading=true (dados carregando por trás)
  useEffect(() => {
    if (!visible || !isLoading || exiting) return;

    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const next = Math.min(
        MAX_PROGRESS_WHILE_LOADING,
        MAX_PROGRESS_WHILE_LOADING * (1 - Math.exp(-elapsed / 1400))
      );
      setProgress(next);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [visible, isLoading, exiting]);

  // Quando os dados estão prontos (isLoading=false), completa e some respeitando tempo mínimo
  useEffect(() => {
    if (!visible || isLoading || exiting) return;

    const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : MIN_VISIBLE_MS;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
    const timer = window.setTimeout(finishAndHide, remaining);
    return () => window.clearTimeout(timer);
  }, [visible, isLoading, exiting, finishAndHide]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.resetPreLoaderState = () => {
        hasShownInCurrentPageLoad = false;
      };
    }
  }, []);

  if (skipPreloader.current || !visible) {
    return null;
  }

  const strokeColor = primaryColor || '#3B82F6';

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-white transition-opacity duration-300 ${
        exiting ? 'opacity-0' : 'opacity-100'
      }`}
      aria-busy={!exiting}
      aria-label="Carregando"
    >
      <div className="text-center">
        {logo && (
          <div className="mb-12">
            <img
              src={logo}
              alt=""
              className="h-32 w-auto mx-auto object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
              fetchPriority="high"
            />
          </div>
        )}

        <div className="relative inline-block">
          <h1
            className="text-5xl md:text-6xl font-bold m-0 p-0"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              color: 'transparent',
              WebkitTextStroke: `1px ${strokeColor}`,
            }}
          >
            {companyName || 'Imobiliária'}
          </h1>

          <h1
            className="absolute top-0 left-0 text-5xl md:text-6xl font-bold overflow-hidden whitespace-nowrap m-0 p-0"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              color: strokeColor,
              WebkitTextStroke: `1px ${strokeColor}`,
              width: `${progress}%`,
              transition: exiting ? 'width 0.35s ease-out' : 'none',
              textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
            }}
          >
            {companyName || 'Imobiliária'}
          </h1>
        </div>
      </div>
    </div>
  );
};

export default PreLoader;
